/**
 * O instante em que a mesa se forma.
 *
 * A fila é a única parte do produto em que a pessoa espera sem fazer
 * nada, e o fim dessa espera é a melhor notícia do fluxo. Antes ela
 * chegava como uma troca de tela silenciosa, direto para o formulário de
 * combinar valor — o momento acontecia e ninguém via.
 *
 * O que a tela mostra é só o que atravessa a mesa: nome e avatar dos
 * dois. Não há saldo do adversário, não há histórico dele, não há "esse
 * jogador ganhou 80% das partidas". Isso é deliberado: exibir o retrospecto
 * de quem está do outro lado transformaria a escolha de aceitar o valor
 * numa aposta sobre a pessoa, e é exatamente assim que se monta a caça ao
 * jogador fraco.
 *
 * A tela some sozinha. Um botão "continuar" faria o jogador pagar com um
 * clique por uma informação que ele não pediu.
 */
import { useEffect, useRef } from 'react';

import { m } from 'motion/react';

import { Avatar } from './Avatar.tsx';
import type { GameType } from '../lib/adapter.ts';
import { GAME_LABEL } from '../lib/format.ts';

export interface LadoDaMesa {
  readonly displayName: string;
  readonly avatar: string;
  readonly avatarColor: string;
}

/** Quanto tempo o anúncio fica na tela antes de sair sozinho. */
const DURACAO_MS = 2_200;

export function PartidaEncontrada({
  eu,
  adversario,
  gameType,
  onFim,
}: {
  eu: LadoDaMesa;
  adversario: LadoDaMesa;
  gameType: GameType;
  onFim: () => void;
}) {
  // O aviso dura DURACAO_MS a partir do momento em que aparece, e a
  // contagem não pode depender da identidade de `onFim`.
  //
  // Com `[onFim]` nas dependências, este efeito virava uma armadilha: quem
  // usa o componente passa `onFim={() => ...}` inline, que é uma função
  // nova a cada render, e `AcertarValor` re-renderiza de segundo em
  // segundo por causa do relógio da negociação. O efeito limpava e
  // reagendava o timeout antes de ele completar 2,2 segundos, então ele
  // **nunca disparava** — e como este anúncio é `fixed inset-0` com fundo
  // opaco, a tela ficava coberta para sempre.
  //
  // A referência guarda a função mais recente sem entrar nas
  // dependências. Assim a contagem começa uma vez, na montagem, e quem
  // chama não precisa saber que memorizar o callback era obrigatório.
  const aoFim = useRef(onFim);
  aoFim.current = onFim;

  useEffect(() => {
    const fim = setTimeout(() => aoFim.current(), DURACAO_MS);
    return () => clearTimeout(fim);
  }, []);

  return (
    <m.div
      className="fixed inset-0 z-50 grid place-items-center px-4"
      style={{ background: 'var(--background)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-lg grid gap-8 text-center">
        <div className="grid gap-1">
          <p className="rotulo">Partida encontrada</p>
          <h2 className="font-bold" style={{ fontSize: 'var(--text-display)', lineHeight: 1.1 }}>
            {GAME_LABEL[gameType]}
          </h2>
        </div>

        <div className="flex items-center justify-center gap-4 sm:gap-8">
          <Lado lado={eu} rotulo="Você" atraso={0} />

          <span
            className="font-bold shrink-0"
            style={{ fontSize: 'var(--text-title)', color: 'var(--brass)' }}
            aria-hidden="true"
          >
            ×
          </span>

          <Lado lado={adversario} rotulo="Adversário" atraso={140} />
        </div>

        <p className="text-muted-foreground">Agora vocês combinam quanto vale.</p>
      </div>
    </m.div>
  );
}

function Lado({
  lado,
  rotulo,
  atraso,
}: {
  lado: LadoDaMesa;
  rotulo: string;
  atraso: number;
}) {
  return (
    <m.div
      className="grid gap-3 justify-items-center min-w-0"
      initial={{ opacity: 0, y: 12, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, delay: atraso / 1000, ease: [0.2, 0.9, 0.3, 1] }}
    >
      <Avatar
        avatar={lado.avatar}
        cor={lado.avatarColor}
        tamanho={88}
        nome={lado.displayName}
      />
      <div className="min-w-0">
        <p className="font-bold truncate max-w-[9rem]">{lado.displayName}</p>
        <p className="rotulo">{rotulo}</p>
      </div>
    </m.div>
  );
}
